import { Button, Modal, Space, Table, Typography } from 'antd';
import { Risk, RiskAction } from '../../models';
import moment from 'moment';
import { useContext, useEffect, useState } from 'react';
import { UserContext } from '../contexts';
import { hasPermission, isSuperadmin } from '../../utils';
import { NewRiskActionPage } from './new-risk-action-page';
import { RiskActionStatus } from './risk-action-status';
import { RiskActionDetailsPage } from './risk-action-details-page';

export function RiskActionsTable(props: { risk?: Risk; onSaved?: () => void }) {
  const { user } = useContext(UserContext);
  const [risk, setRisk] = useState(props.risk);
  const [canAddSolution, setCanAddSolution] = useState(false);
  const [newSolutionModalOpen, setNewSolutionModalOpen] = useState(false);
  const [solutionDetailsModalOpen, setSolutionDetailsModalOpen] = useState<string>();

  const { Text } = Typography;

  useEffect(() => {
    setRisk(props.risk);
  }, [props.risk]);

  useEffect(() => {
    if (risk?.riskActions?.some(s => ['PENDING', 'ACCEPTED', 'EVALUATING'].includes(s.status))) {
      setCanAddSolution(false);
      return;
    }
    if (hasPermission(user, 'riskActions.edit') || isSuperadmin(user) || risk?.area?.responsibleUser?.id === user?.id) {
      setCanAddSolution(true);
    }
  }, [risk?.area?.responsibleUser?.id, risk?.riskActions, user]);

  const openNewSolutionModal = () => {
    setNewSolutionModalOpen(true);
  };

  const closeNewSolutionModal = () => {
    setNewSolutionModalOpen(false);
  };

  const openSolutionDetailsModal = (riskActionId: string) => {
    setSolutionDetailsModalOpen(riskActionId);
  };

  const closeSolutionDetailsModal = () => {
    setSolutionDetailsModalOpen(undefined);
  };

  const onRiskActionSaved = () => {
    closeNewSolutionModal();
    if (props.onSaved) {
      props.onSaved();
    }
  };

  const tableColumns: any[] = [
    {
      title: 'Çözüm yöntemi',
      dataIndex: ['solutionType'],
      key: 'solutionType',
      render: (solutionType: string) => {
        if (solutionType === 'FIVE_S') {
          return <span>5S</span>;
        } else if (solutionType === 'VISUAL') {
          return <span>GÖRSEL</span>;
        } else {
          return <span>{solutionType}</span>;
        }
      },
    },
    {
      title: 'Çözüm sorumlusu',
      dataIndex: ['responsibleUser', 'fullNameId'],
      key: 'responsibleUser',
    },
    {
      title: 'Durum',
      dataIndex: 'status',
      key: 'status',
      render: (status: string, riskAction: RiskAction) => (
        <Space direction="vertical">
          <RiskActionStatus riskAction={riskAction} />
          {status === 'PENDING' ? (
            <Text type="secondary">Hedef tarih: {moment(riskAction.targetDate).format('DD.MM.yyyy')}</Text>
          ) : status === 'EVALUATING' ? (
            <span>Gönderilme: {moment(riskAction.solvedAt).format('DD.MM.yyyy HH:mm')}</span>
          ) : status === 'ACCEPTED' ? (
            <Text type="secondary">
              {riskAction.evaluatedBy.fullNameId} - {moment(riskAction.evaluatedAt).format('DD.MM.yyyy')}
            </Text>
          ) : status === 'REJECTED' ? (
            <Space direction="vertical">
              <Text type="secondary">
                {riskAction.evaluatedBy.fullNameId} - {moment(riskAction.evaluatedAt).format('DD.MM.yyyy')}
              </Text>
              <Text type="secondary" style={{ fontSize: '0.8rem' }}>
                {riskAction.evaluationMessage}
              </Text>
            </Space>
          ) : null}
        </Space>
      ),
    },
    {
      title: 'Oluşturma',
      dataIndex: ['createdAt'],
      key: 'createdAt',
      render: (createdAt: Date) => moment(createdAt).format('DD.MM.yyyy HH:mm'),
    },
    {
      title: '',
      dataIndex: ['id'],
      key: 'actions',
      render: (id: string, riskAction: RiskAction) => (
        <Button type="link" onClick={() => openSolutionDetailsModal(riskAction.id)}>
          Detaylar
        </Button>
      ),
    },
  ];

  return (
    <>
      <Space direction="vertical" style={{ width: '100%' }}>
        <Table columns={tableColumns} dataSource={risk?.riskActions} pagination={false} />
        {canAddSolution && (
          <Button onClick={openNewSolutionModal} type="primary">
            Çözüm önerisi ekle
          </Button>
        )}
      </Space>
      <Modal title="Yeni çözüm önerisi" visible={newSolutionModalOpen} footer={null} onCancel={closeNewSolutionModal} destroyOnClose={true}>
        <NewRiskActionPage risk={risk} onSaved={onRiskActionSaved} />
      </Modal>
      <Modal
        title="Çözüm önerisi"
        visible={!!solutionDetailsModalOpen}
        width={'50%'}
        footer={null}
        onCancel={closeSolutionDetailsModal}
        destroyOnClose={true}
      >
        <RiskActionDetailsPage risk={risk} riskActionId={solutionDetailsModalOpen} onSaved={onRiskActionSaved} />
      </Modal>
    </>
  );
}
